import * as React from 'react'
import { WPSClient, Logger } from 'DVWebNodeUtils'
import { Button, Modal, PrimaryButton, convertString } from 'DVWebNodeUI'
import * as cn from 'classnames'

interface DestructuredURL {
    partialURL: string,
    query: { [key: string]: string }
}

interface SearchHistoryStrings {
  recent: string,
  suggested: string,
  clear: string,
  clearInfoTitle: string,
  clearInfoButton: string,
  clearInfoMessage: string,
  cancel: string,
  clearInfoAck: string
}

interface SearchHistory {
  treatment: string,
  strings: SearchHistoryStrings
}

export interface SearchBarProps {
    hasSignUpCTA: Boolean,
    searchFormAction?: DestructuredURL,
    searchFormPlaceholder: string,
    searchPhrase?: string,
    searchSuggestionsEndpoint?: DestructuredURL,
    altText: string,
    context: any,
    logger?: Logger,
    searchHistory: SearchHistory
}

interface SearchSuggestion {
  text: string,
  href: string
}

interface SearchBarState {
  searchHistory: Array<SearchSuggestion>,
  searchSuggested: Array<SearchSuggestion>,
  selectedSearchSuggestionId: number,
  searchSuggestionsFetchHandle?: number
}

interface MouseEvent extends Event {
    x: number,
    y: number,
    keyCode: number,
    target: any
}

export const KEY_ARROW_UP = 38
export const KEY_ARROW_DOWN = 40
export const KEY_ENTER = 13

const MIN_THROTTLE_TIME = 200
const MIN_FETCH_SEARCH_SUG_CHAR_COUNT = 3
const MAX_SUGGESTIONS = 50

export const issAjaxErrorCounterName = 'AVNavIssAjaxErrors'

export class SearchBar extends React.Component<SearchBarProps, SearchBarState> {
  state: SearchBarState
  searchBarContainer: HTMLDivElement
  searchInput: HTMLInputElement
  WPS: any

  static defaultProps: SearchBarProps = {
    hasSignUpCTA: true,
    searchFormAction: { partialURL: '', query: {}},
    searchFormPlaceholder: '',
    searchPhrase: '',
    altText: '',
    context: {},
    searchHistory: { treatment: 'C', strings: {
      recent: '',
      suggested: '',
      clear: '',
      clearInfoTitle: '',
      clearInfoButton: '',
      clearInfoMessage: '',
      cancel: '',
      clearInfoAck: ''} }
  }

  constructor(props: SearchBarProps) {
    super(props)

    this.state = {
      searchHistory: [],
      searchSuggested: [],
      selectedSearchSuggestionId: -1,
      searchSuggestionsFetchHandle: null
    }

    this.handleInput = this.handleInput.bind(this)
    this.handleReset = this.handleReset.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.WPS = new WPSClient()
  }

  searchHistoryList: HTMLOListElement
  searchSuggestedList: HTMLOListElement

  searchForm: HTMLFormElement
  recentSubheading: HTMLLIElement

  hasSearchHistory: Boolean = false
  hasSearchSuggested: Boolean = false
  searchHistoryOn: Boolean = this.props.searchHistory.treatment == 'T1'

  modal = React.createRef<Modal>()
  openModal = () => {
    this.modal.current.open()
  }
  closeModal = () => {
    this.modal.current.close()
  }

  render() {
    const { props, state } = this

    const hiddenFields = Object.keys(props.searchFormAction.query).map(name => (
      <input type="hidden" name={name} key={name} value={props.searchFormAction.query[name]} />
    ))

    const className = this.props.hasSignUpCTA ? "pv-nav-search pv-nav-search-CTA-button" : "pv-nav-search"

    const searchHistoryClassName = (this.hasSearchHistory && this.searchHistoryOn) ? "pv-nav-search-history" : ""
    const searchSuggestedClassName = (this.hasSearchSuggested && this.searchHistoryOn) ? "pv-nav-search-suggested" : ""
    const searchSuggestionClassName = `pv-nav-search-suggestions ${searchHistoryClassName} ${searchSuggestedClassName}`
    const searchSuggestionsNonEmpty = this.hasSearchHistory || this.hasSearchSuggested

    const searchStandardSuggestionsList =
    <ol>
      <li className="pv-nav-search-subheading suggested">
        {this.props.searchHistory.strings.suggested}
      </li>  
      <ol ref={ ol => { this.searchSuggestedList = ol } }>
      {
        state.searchSuggested.map((searchSuggestion, index) => {
          const searchSuggestionLinkClass = cn({
            'pv-highlighted': (
              index === state.selectedSearchSuggestionId
            )
          },
            'pv-nav-search-suggestion-item'
          )

          const searchSuggestionHTML = convertString(searchSuggestion.text)
          return (
            <li className="pv-nav-search-suggestion suggested">
              <a href={searchSuggestion.href} className={searchSuggestionLinkClass}
                dangerouslySetInnerHTML={{ __html: `${searchSuggestionHTML}` }}
              />
            </li>
          )
        })
      }       
      </ol>
    </ol>
    
    const clearAllModal = 
      <Modal className="pv-nav-search-clear-modal" modalId="search-history"
        ref={this.modal}
        footer={
          <div>
            <PrimaryButton onClick={() => {
              this.clearSearchHistory();
              this.closeModal()}}> 
              {this.props.searchHistory.strings.clearInfoButton}
            </PrimaryButton>
            <Button onClick={() => this.closeModal()}> {this.props.searchHistory.strings.cancel} </Button>
          </div>
        }
        header={this.props.searchHistory.strings.clearInfoTitle}>
        {this.props.searchHistory.strings.clearInfoMessage}
      </Modal>

    const searchHistorySuggestionsList =
    <ol>
      <li className="pv-nav-search-subheading history">
        {this.props.searchHistory.strings.recent}
        <button className="pv-nav-search-clear" onClick={() => this.openModal()}>
          {this.props.searchHistory.strings.clear}
        </button>
        {clearAllModal}
      </li>
      <ol ref={ ol => { this.searchHistoryList = ol } }>
      {
        state.searchHistory.map((searchSuggestion, index) => {
          const searchSuggestionLinkClass = cn({
            'pv-highlighted': (
              index + state.searchSuggested.length === state.selectedSearchSuggestionId
            )
          },
            'pv-nav-search-suggestion-item'
          )
          const searchSuggestionHTML = convertString(searchSuggestion.text)
          const query = searchSuggestionHTML.replace(/<\/?strong>/g,'')
          return (
            <li className="pv-nav-search-suggestion history">
              <input type="reset" className="pv-nav-search-delete-history"
              name="reset" value="Cancel"
              onClick={() => {this.removeQueryFromHistory(query); this.fetchSearchHistory()}} />
              <a href={searchSuggestion.href} className={searchSuggestionLinkClass}
                dangerouslySetInnerHTML={{ __html: `${searchSuggestionHTML}` }}
              />
            </li>
          )
        })
      }
      </ol>    
    </ol>

    return (
      <div className={className} ref={ div => { this.searchBarContainer = div } }>
        <form className="pv-nav-search-form" method="GET" role="search"
          action={props.searchFormAction.partialURL} onSubmit={this.handleSubmit}
          ref={ form => { this.searchForm = form } }>
          <label className="pv-nav-screen-reader-only" htmlFor="pv-search-nav">{props.altText}</label>
          <input type="search" className={
            cn({ "pv-nav-search-query-search-history": this.hasSearchHistory && this.searchHistoryOn },
              "pv-nav-search-query")
          }
            id="pv-search-nav" required={true}
            placeholder={props.searchFormPlaceholder} name="phrase" autoComplete="off"
            onInput={this.handleInput} onKeyUp={this.handleInput}
            onFocus={this.handleInput} onPaste={this.handleInput} onCut={this.handleInput}
            ref={ input => { this.searchInput = input } } />
          {hiddenFields}
          <input type="reset" className="pv-nav-search-reset pv-nav-search-reset"
            name="reset" value="Cancel"
            onClick={this.handleReset} onReset={this.handleReset} />
          <input type="submit" className="pv-nav-search-submit pv-nav-screen-reader-only" />
        </form>
        <div className={searchSuggestionClassName}>
        <ol className={searchSuggestionsNonEmpty ? `non-empty` : ``}>
          <li> {searchStandardSuggestionsList} </li>
          <li> {searchHistorySuggestionsList} </li>
        </ol>
        </div>
      </div>
    )
  }

  componentDidMount() {
    window.addEventListener('click', this.handleDocumentClick)

    if (this.props.searchPhrase && this.searchInput) {
      // Value cannot be just simply assigned in render
      // because value needs to be cleared on reset
      // instead of being set back to the original search phrase
      this.searchInput.value = this.props.searchPhrase
    }
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleDocumentClick)
  }

  handleDocumentClick(event: MouseEvent) {
    const { searchInput } = this
    // Search suggestions should be cleared
    // if user clicks on anywhere else other than the Search Bar itself
    if (!this.searchBarContainer.contains(event.target)) {
      this.clearSearchSuggestions()
    } 
    // Trigger DV_WEB_SEARCH_HISTORY_209978 weblab on use of search form
    if (this.searchForm.contains(event.target) && ['C', 'T1'].includes(this.props.searchHistory.treatment)) {
      (window as unknown as WindowCSM).ue.trigger('DV_WEB_SEARCH_HISTORY_209978', `${this.props.searchHistory.treatment}`)
    }
    // If search suggestion is clicked it should be added to search history
    if (this.searchSuggestedList.contains(event.target)) {
      searchInput.value = (event.target as HTMLElement).innerText
      this.updateSearchHistory(searchInput.value)
    }
    // If search history is clicked it should be submitted
    else if (this.searchHistoryList.contains(event.target) && !this.searchHistoryList == event.target) {
      this.submitSearchHistory((event.target as HTMLElement).innerText)
      this.updateSearchHistory(searchInput.value)
    }
  }

  handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    // Kill search submission when a search suggestion is being selected
    if (this.state.selectedSearchSuggestionId >= 0) {
      event.preventDefault()
    }
    else {
      this.updateSearchHistory(this.searchInput.value)
    }
  }

  handleReset() {
    // Clear results if reset button is clicked
    this.searchInput.value = ''
    this.clearSearchSuggestions()
  }

  handleInput(event: any) {
    const { props, state, searchInput } = this

    if (!props.searchSuggestionsEndpoint) {
      // Return immediately because search suggestion is disabled
      return
    }

    if (event.keyCode && (event.keyCode === KEY_ARROW_UP || event.keyCode === KEY_ARROW_DOWN)) {
      const totalSuggestionsLength = state.searchHistory.length + state.searchSuggested.length
      // Suggestion can only be selected if there are suggestions
      if (totalSuggestionsLength > 0) {
        if (event.keyCode === KEY_ARROW_UP) {
          this.setState({ selectedSearchSuggestionId: (state.selectedSearchSuggestionId > 0
            ? state.selectedSearchSuggestionId - 1 : totalSuggestionsLength - 1) })
        } else if (event.keyCode === KEY_ARROW_DOWN) {
          this.setState({
            selectedSearchSuggestionId:
              state.selectedSearchSuggestionId < totalSuggestionsLength - 1
                ? state.selectedSearchSuggestionId + 1 : 0
          })
        }
        event.preventDefault()
      }
    } else if (event.keyCode && event.keyCode === KEY_ENTER) {
      // The suggestion can only be searched for if there is a selected one
      if (state.selectedSearchSuggestionId >= 0) {
        if (state.selectedSearchSuggestionId < state.searchSuggested.length) {
          // If WPS suggestions selected, follow href
          const selectedSearchSuggestion = state.searchSuggested[state.selectedSearchSuggestionId]
          //this.setSearchInputValue(this.searchSuggestedList, state.selectedSearchSuggestionId + 1)
          this.searchInput.value = (this.searchSuggestedList.querySelector(
            `li:nth-child(${state.selectedSearchSuggestionId + 1}) a`) as HTMLElement).innerText
          window.location.assign(selectedSearchSuggestion.href)
        } else {
          // If recent selected, submit phrase
          //this.setSearchInputValue(this.searchHistoryList, state.selectedSearchSuggestionId - this.state.searchSuggested.length + 1)
          this.submitSearchHistory((this.searchHistoryList.querySelector(
            `li:nth-child(${state.selectedSearchSuggestionId - this.state.searchSuggested.length + 1}) a`) as HTMLElement).innerText)
        }
        this.setState({ selectedSearchSuggestionId: -1 })
        this.updateSearchHistory(searchInput.value)
        event.preventDefault()
      }
     } else {
      this.fetchSearchSuggestions(event.target.value)
      this.setState({ selectedSearchSuggestionId: -1 })
    }
  }

  killSearchSuggestionsFetch() {
    if (this.state.searchSuggestionsFetchHandle) {
      window.clearTimeout(this.state.searchSuggestionsFetchHandle)
    }
  }

  initiateHistory() {
    if (!localStorage.getItem('pos')) {
      localStorage.setItem('pos', '-1')
    }
  }

  removeQueryFromHistory(query: string) {
    var pos: number = Number(localStorage.getItem('pos'))
    for (let i = 0; pos-i >= 0 && i < MAX_SUGGESTIONS; i++) {
      if (Boolean(localStorage.getItem(`query${pos-i}`))) {
        if (query == localStorage.getItem(`query${pos-i}`)) {
          localStorage.removeItem(`query${pos-i}`)
          for (let j = i; j < MAX_SUGGESTIONS; j++)
            if (Boolean(localStorage.getItem(`query${pos-(j+1)}`))) {
              localStorage.setItem(`query${pos-j}`, localStorage.getItem(`query${pos-(j+1)}`))
              localStorage.removeItem(`query${pos-(j+1)}`)
            }
        }
      }
    }
  }

  // Store query and href in search history
  // Delete old/duplicate queries
  updateSearchHistory(query: string) {
    const sanitisedQuery = convertString(query.replace(/[@#%^*(){}|<>]/g,'').toLowerCase())
    const isSafe = (/\S/.test(sanitisedQuery))
    // Only store if satisfies constraints
    if (isSafe) {
      this.initiateHistory()
      var pos: number = Number(localStorage.getItem('pos')) + 1
      localStorage.setItem(`query${pos}`, sanitisedQuery)
      // Remove duplicates
      this.removeQueryFromHistory(sanitisedQuery)
      localStorage.setItem('pos', `${pos}`)
      // Store at most MAX_SUGGESTIONS search phrases
      localStorage.removeItem(`query${pos-MAX_SUGGESTIONS}`)

    }
  }

  submitSearchHistory( phrase: string ) {
    const { searchInput } = this
    const inputLength = searchInput.value.length
    searchInput.value = phrase
    this.searchForm.action = this.searchForm.action.concat(`_hist_${inputLength}`)
    this.searchForm.submit()
  }

  fetchSearchHistory() {
    const searchPhrase = convertString(this.searchInput.value.toLowerCase())
    this.initiateHistory()
    const pos = Number(localStorage.getItem('pos'))
    var searchHistory = []
    const maxToShow = this.hasSearchSuggested ? 5 : 10
    for (let i = 0; pos-i >= 0 && i < MAX_SUGGESTIONS ; i++) {
      if (Boolean(localStorage.getItem(`query${pos-i}`))) {
        var text = localStorage.getItem(`query${pos-i}`)
        if (text.includes(searchPhrase)) {
          text = text.replace(searchPhrase, "**" + searchPhrase + "**")
          searchHistory.push({
            text: text,
            href: null
          })
        }
      }
    }
    this.hasSearchHistory = searchHistory.length>0
    this.setState({searchHistory: searchHistory.slice(0,maxToShow)})
  }

  clearSearchHistory() {
    const pos = Number(localStorage.getItem('pos'))
    for (let i = 0; pos-i >= 0 && i < MAX_SUGGESTIONS ; i++) {
      localStorage.removeItem(`query${pos-i}`)
    }
    localStorage.setItem('pos', '-1')
    this.hasSearchHistory = false
    this.fetchSearchHistory()
  }

  clearSearchSuggestions(killHistory: Boolean = true) {
    this.killSearchSuggestionsFetch()
    this.hasSearchSuggested = false
    this.setState({ searchSuggested: [] })
    if (killHistory) {
      this.hasSearchHistory = false
      this.setState({ searchHistory: [] })
    }
  }

  fetchSearchSuggestions(searchString: string) {
    const that = this
    if (this.searchHistoryOn) {
      this.fetchSearchHistory()
    }
    if (searchString.length >= MIN_FETCH_SEARCH_SUG_CHAR_COUNT) {
      this.killSearchSuggestionsFetch()

      const searchSuggestionsFetchHandle = window.setTimeout(async () => {
        try {
          const response = await this.WPS.SearchSuggestions({
            phrase: searchString,
            maxSuggestions: 10,
            context: this.props.context
          });
          const searchSuggested = response
            ? response.hasOwnProperty('suggestions')
              ? response.suggestions
              : response
            : []
            this.searchHistoryOn ? that.setState({ searchSuggested : searchSuggested.slice(0,5)})
            : that.setState({ searchSuggested })
        } catch(error) {
          this.props.logger
            .error('[atv_nav_iss_ajax_error]', error)
            .count(issAjaxErrorCounterName, 1, true)
        }
      }, MIN_THROTTLE_TIME)

      this.setState({ searchSuggestionsFetchHandle })
      this.hasSearchSuggested = true

    } else {
      this.clearSearchSuggestions(false)
    } 
  }
}
